import React from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Dropdown } from 'react-bootstrap';
import { BsChevronRight } from 'react-icons/bs';

/**
 * Row-action chevron menu for DataTable rows (Ver detalles / Editar /
 * Eliminar, etc.). Shared so every entity list gets the fix below in one
 * place instead of duplicated per screen.
 *
 * `strategy: 'fixed'` fixes a real bug: DataTable wraps its table in a
 * `tw:overflow-x-auto` div for wide-table scrolling. Per the CSS overflow
 * spec, setting only `overflow-x` to a non-`visible` value forces the
 * browser to compute `overflow-y` as `auto` too — so on a table short enough
 * that this menu is taller than it (e.g. a single row), the menu's ink
 * overflow made that wrapper grow an unwanted vertical scrollbar and clip
 * the menu. `position: fixed` removes the menu from that ancestor's
 * scrollable-overflow calculation entirely (its containing block becomes the
 * viewport instead of any scrolling ancestor), so it can never trigger that
 * scrollbar, regardless of how short the table is.
 *
 * That alone regressed the menu's anchoring, and the cause is a genuine
 * Popper.js/react-bootstrap interaction bug, confirmed by reading
 * `@popperjs/core`'s source and measuring the live DOM: react-bootstrap's
 * `.dropdown` wrapper (`.entity-list-row-actions`) is `position: relative`
 * (needed for the default `absolute` strategy). For `strategy: 'fixed'`,
 * Popper computes the toggle's position relative to the *popper element's*
 * native `offsetParent` (`getOffsetParent` in
 * `@popperjs/core/lib/dom-utils/getOffsetParent.js`) — and native
 * `offsetParent` treats ANY `position !== static` ancestor as a stopping
 * point, even though only `transform`/`filter`/`perspective`/etc. actually
 * establish a containing block for `position: fixed` per the CSS spec.
 * Since `.entity-list-row-actions` sits at almost the exact same position as
 * the toggle, Popper subtracts the wrapper's own rect from the toggle's
 * rect and gets ~0 — i.e. it computes the toggle as if it were pinned to
 * the very top of the page, so the menu renders up there instead of next to
 * the toggle.
 *
 * Portaling the menu to `document.body` sidesteps this entirely: the
 * popper element's offsetParent is then resolved from `document.body`
 * (`position: static`), which `getOffsetParent` maps straight to the real
 * browser `window`/viewport, so the toggle's position comes through
 * correctly and the menu anchors right next to it.
 *
 * `POPPER_CONFIG` is module-level (not an inline literal) so every render of
 * every row's menu shares one stable object reference, matching react-
 * bootstrap's own guidance to avoid handing `popperConfig` a new object each
 * render.
 */
const POPPER_CONFIG = { strategy: 'fixed' };

const PortalMenu = React.forwardRef(function PortalMenu({ children, ...props }, ref) {
	return createPortal(
		<div ref={ref} {...props}>
			{children}
		</div>,
		document.body,
	);
});

function RowActionsMenu({ items }) {
	const visibleItems = items.filter(Boolean);
	if (visibleItems.length === 0) {
		return null;
	}

	return (
		<Dropdown align='end' className='entity-list-row-actions'>
			<Dropdown.Toggle
				as='button'
				bsPrefix='entity-list-row-toggle'
				className='tw:inline-flex tw:items-center tw:justify-center tw:w-[26px] tw:h-[26px] tw:rounded-[7px] tw:border-0 tw:bg-transparent tw:text-ink-2 hover:tw:bg-surface-3 focus:tw:bg-surface-3 focus:tw:outline-none'
			>
				<BsChevronRight />
			</Dropdown.Toggle>
			<Dropdown.Menu as={PortalMenu} className='entity-list-dropdown-menu' popperConfig={POPPER_CONFIG}>
				{visibleItems.map(({ label, onClick, danger }) => (
					<Dropdown.Item
						key={label}
						className={classnames('entity-list-dropdown-item', danger && 'entity-list-dropdown-item-danger')}
						onClick={onClick}
					>
						{label}
					</Dropdown.Item>
				))}
			</Dropdown.Menu>
		</Dropdown>
	);
}

RowActionsMenu.propTypes = {
	items: PropTypes.arrayOf(
		PropTypes.oneOfType([
			PropTypes.oneOf([false, null, undefined]),
			PropTypes.shape({
				label: PropTypes.string.isRequired,
				onClick: PropTypes.func.isRequired,
				danger: PropTypes.bool,
			}),
		]),
	).isRequired,
};

export default RowActionsMenu;
